import { Controller } from "@hotwired/stimulus"

const PHASES = {
  1: {
    name: "Phase 1 · Panic",
    color: "#E24B4A",
    recommendation: "Avoid chasing",
    description: "Oil shock triggered. Dollar strengthening, yields elevated. Mechanical suppression underway. Gold can fall despite bad news.",
    advice: "Do not buy the initial dip or panic-sell. Institutions are watching, not acting. Wait for absorption to begin."
  },
  2: {
    name: "Phase 2 · Absorption",
    color: "#D4872A",
    recommendation: "Hold — do not sell",
    description: "Volatility slows down. Gold may still wobble and silver can remain weak even as the worst pressure fades.",
    advice: "Historically this phase is where retail investors make their worst decision by selling too early. Stay patient."
  },
  3: {
    name: "Phase 3 · Structural bid",
    color: "#5A8F1E",
    recommendation: "Accumulate gradually",
    description: "Forced selling fades. Dollar and yields lose strength. Gold and silver begin confirming the turn.",
    advice: "This is usually the best phase for disciplined buying. Add in pieces instead of chasing strong days."
  },
  4: {
    name: "Phase 4 · New highs",
    color: "#1A9068",
    recommendation: "Hold — ride the trend",
    description: "Full reversal is underway. Lower yields, weaker dollar, and broader flows support metals.",
    advice: "By the time this looks obvious in headlines, a lot of the move is already gone. Manage the position, do not overtrade it."
  }
}

export default class extends Controller {
  static targets = [
    "dxyCard",
    "yieldCard",
    "dxyValue",
    "dxyNote",
    "yieldValue",
    "dxyBadge",
    "yieldBadge",
    "phasePill",
    "phasePillName",
    "phasePillRecommendation",
    "recommendationValue",
    "recommendationNote",
    "phaseTitle",
    "scoreValue",
    "confidenceValue",
    "phaseDescription",
    "phaseAdvice",
    "phaseCursor",
    "sliderDxy",
    "sliderYield",
    "sliderWeeks",
    "sliderOutputDxy",
    "sliderOutputYield",
    "sliderOutputWeeks"
  ]

  static values = {
    initialInputs: Object,
    dxyAvailable: Boolean
  }

  connect() {
    if (!this.hasSliderDxyTarget || !this.hasSliderYieldTarget || !this.hasSliderWeeksTarget) {
      return
    }

    if (!this.dxyAvailableValue) {
      this.renderMissingDxy()
      return
    }

    this.update()
  }

  update() {
    const dxy = parseFloat(this.sliderDxyTarget.value)
    const yield10y = parseFloat(this.sliderYieldTarget.value)
    const weeks = parseInt(this.sliderWeeksTarget.value, 10)

    this.sliderOutputDxyTarget.textContent = dxy.toFixed(1)
    this.sliderOutputYieldTarget.textContent = `${yield10y.toFixed(2)}%`
    this.sliderOutputWeeksTarget.textContent = `${weeks} wks`

    const dxyStatus = this.dxyStatus(dxy)
    const yieldStatus = this.yieldStatus(yield10y)
    const phase = this.determinePhase(dxy, yield10y, weeks)
    const phaseInfo = PHASES[phase]
    const score = this.scorePercent(dxy, yield10y)

    this.applyCardStatus(this.dxyCardTarget, this.dxyBadgeTarget, dxyStatus, this.statusLabel(dxyStatus))
    this.applyCardStatus(this.yieldCardTarget, this.yieldBadgeTarget, yieldStatus, this.statusLabel(yieldStatus))

    this.dxyValueTarget.textContent = dxy.toFixed(1)
    if (this.hasDxyNoteTarget) {
      this.dxyNoteTarget.textContent = "A weaker dollar helps metals. Rough guide: below 100 bullish, above 105 headwind."
    }
    this.yieldValueTarget.textContent = `${yield10y.toFixed(2)}%`

    this.phasePillTarget.style.setProperty("--active-color", phaseInfo.color)
    this.phasePillNameTarget.textContent = phaseInfo.name
    this.phasePillRecommendationTarget.textContent = phaseInfo.recommendation

    this.recommendationValueTarget.textContent = phaseInfo.recommendation
    this.recommendationNoteTarget.textContent = phaseInfo.description
    this.phaseTitleTarget.textContent = phaseInfo.name
    this.scoreValueTarget.textContent = `${score} / 100`
    this.confidenceValueTarget.textContent = this.confidenceFor(score)
    this.phaseDescriptionTarget.innerHTML = `<strong>${phaseInfo.recommendation}.</strong> ${phaseInfo.description}`
    this.phaseAdviceTarget.textContent = phaseInfo.advice
    this.phaseCursorTarget.style.left = `${this.phasePercent(phase, weeks)}%`
    this.phaseCursorTarget.style.color = phaseInfo.color

    this.element.querySelectorAll("[data-phase-segment]").forEach((segment) => {
      segment.classList.toggle("active", parseInt(segment.dataset.phaseSegment, 10) === phase)
    })
  }

  reset(event) {
    event.preventDefault()

    const defaults = this.initialInputsValue || {}
    if (Object.keys(defaults).length === 0) return

    this.sliderDxyTarget.value = defaults.dxy_level
    this.sliderYieldTarget.value = defaults.yield_10y
    this.sliderWeeksTarget.value = defaults.weeks_since_shock

    if (!this.dxyAvailableValue) {
      this.renderMissingDxy()
      return
    }

    this.update()
  }

  renderMissingDxy() {
    const yield10y = parseFloat(this.sliderYieldTarget.value)
    const weeks = parseInt(this.sliderWeeksTarget.value, 10)

    this.sliderOutputDxyTarget.textContent = this.sliderDxyTarget.value
    this.sliderOutputYieldTarget.textContent = `${yield10y.toFixed(2)}%`
    this.sliderOutputWeeksTarget.textContent = `${weeks} wks`

    this.applyCardStatus(this.dxyCardTarget, this.dxyBadgeTarget, "neu", "Unavailable")
    this.applyCardStatus(this.yieldCardTarget, this.yieldBadgeTarget, this.yieldStatus(yield10y), this.statusLabel(this.yieldStatus(yield10y)))

    this.dxyValueTarget.textContent = "Unavailable"
    if (this.hasDxyNoteTarget) {
      this.dxyNoteTarget.textContent = "DXY is unavailable from the latest refresh. Refresh again or move the slider to test a manual scenario."
    }
    this.yieldValueTarget.textContent = `${yield10y.toFixed(2)}%`

    this.phasePillTarget.style.setProperty("--active-color", "#D4872A")
    this.phasePillNameTarget.textContent = "Indicator incomplete · DXY unavailable"
    this.phasePillRecommendationTarget.textContent = "Set DXY manually or refresh again"

    this.recommendationValueTarget.textContent = "Set DXY manually or refresh again"
    this.recommendationNoteTarget.textContent = "DXY is unavailable. Refresh again or set DXY manually to test a scenario."
    this.phaseTitleTarget.textContent = "Indicator incomplete · DXY unavailable"
    this.scoreValueTarget.textContent = "n/a"
    this.confidenceValueTarget.textContent = "Incomplete"
    this.phaseDescriptionTarget.innerHTML = "<strong>Set DXY manually or refresh again.</strong> The indicator is missing a current DXY reading, so the phase cannot be trusted yet."
    this.phaseAdviceTarget.textContent = "Refresh again or move the DXY slider manually if you want to test a scenario."
    this.phaseCursorTarget.style.left = "0%"
    this.phaseCursorTarget.style.color = "#D4872A"

    this.element.querySelectorAll("[data-phase-segment]").forEach((segment) => {
      segment.classList.remove("active")
    })
  }

  determinePhase(dxy, yield10y, weeks) {
    const bullCount = [dxy < 100, yield10y < 3.5].filter(Boolean).length
    const bearCount = [dxy > 105, yield10y > 4.2].filter(Boolean).length
    let phase = this.basePhaseFor(weeks)

    if (bearCount === 2) phase -= 1
    if (bullCount === 2) phase += 1

    if (phase === 3) {
      if (bearCount === 1) phase -= 1
      if (bullCount === 1 && weeks > 20) phase += 1
    }

    phase = Math.max(1, Math.min(4, phase))
    if (weeks <= 4) phase = Math.min(phase, 2)
    if (weeks <= 12 && bullCount < 2) phase = Math.min(phase, 3)
    if (weeks > 52 && bullCount >= 1) phase = Math.max(phase, 3)
    if (weeks > 52 && bearCount === 2) phase = Math.min(phase, 3)

    return phase
  }

  basePhaseFor(weeks) {
    if (weeks <= 4) return 1
    if (weeks <= 12) return 2
    if (weeks <= 52) return 3
    return 4
  }

  phasePercent(phase, weeks) {
    const bases = { 1: 0, 2: 25, 3: 50, 4: 75 }
    const ranges = { 1: 4, 2: 8, 3: 40, 4: 52 }
    const offsets = { 1: 0, 2: 4, 3: 12, 4: 52 }
    const weeksInPhase = Math.min(Math.max(weeks - offsets[phase], 0), ranges[phase])
    return bases[phase] + Math.min((weeksInPhase / ranges[phase]) * 25, 24)
  }

  dxyStatus(value) {
    if (value < 100) return "bull"
    if (value > 105) return "bear"
    return "neu"
  }

  yieldStatus(value) {
    if (value < 3.5) return "bull"
    if (value > 4.2) return "bear"
    return "neu"
  }

  scorePercent(dxy, yield10y) {
    const components = [
      dxy < 100 ? 2 : dxy > 105 ? 0 : 1,
      yield10y < 3.5 ? 2 : yield10y > 4.2 ? 0 : 1
    ]
    return Math.round((components.reduce((sum, value) => sum + value, 0) / 4) * 100)
  }

  confidenceFor(score) {
    if (score >= 75) return "High"
    if (score >= 40) return "Moderate"
    return "Low"
  }

  applyCardStatus(card, badge, status, label) {
    card.classList.remove("gi-metric-card--bull", "gi-metric-card--neu", "gi-metric-card--bear")
    badge.classList.remove("gi-badge--bull", "gi-badge--neu", "gi-badge--bear")
    card.classList.add(`gi-metric-card--${status}`)
    badge.classList.add(`gi-badge--${status}`)
    badge.textContent = label
  }

  statusLabel(status) {
    return {
      bull: "Bullish",
      neu: "Neutral",
      bear: "Bearish"
    }[status]
  }
}
